import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import { ReactComponent as MenuHamburgerIcon } from 'assets/icons/menu-hamburger.svg';
import { ReactComponent as NotificationIcon } from 'assets/icons/nav/notification.svg';
import { ReactComponent as Shop } from 'assets/icons/shop/shop.svg';
import { Section } from './navbar.interfaces';
import { useNavbarOptions } from 'hooks/useNavbarOptions';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import {
  COURSES,
  DASHBOARD,
  LOGOUT,
  MESSAGES,
  MY_ACCOUNT_PROFILE,
  MY_APPOINTMENTS,
  MY_SKIN_MY_VISIT_SUMMARIES,
  NOTIFICATIONS,
  PROVIDERS,
  SHOP,
  TEST_KITS_MANAGEMENT_TEST_KITS,
} from 'utilities/routes';
import { useClearAllSavedDnaScannedDataForUserIdMutation } from 'graphql/generated/hasura';
import { useAuth } from 'auth/context/AuthContext';
import Dropdown from 'components/dropdown/dropdownComponent';
import useDropdown from 'components/dropdown/useDropdown';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CodexFeatureFlags } from 'utilities/interfaces';
import { useNotificationCount } from 'notifications/contexts/NotificationsContext';
import { DropdownBodyContainer } from '../dropdown/DropdownBodyContainer';
import { NotificationNavbarDropdown } from '../../app/notifications/components/NotificationaNavbarDropdown/NotificationNavbarDropdown';
import { AccountCard } from './NavbarProfileDropdownComponents/AccountCard';
import { AccountDropdown } from './NavbarProfileDropdownComponents/AccountDropdown';
import { PatientChatModal } from '../PatientChatModal';

import { ReactComponent as EmailGrayIcon } from 'assets/icons/nav/email.svg';
import { useIndicators } from '../../context/UserIndicatorContext';
import PatientAvatarMobile from './PatientAvatarMobile';
import { Roles } from '../../firebase/interfaces';
import { useGetCartProductsQuery } from '../../graphql/generated/remote-schema-hasura';
import ShopModal from '../Shop/ShopModal';
import ContactSupport from '../footer/ContactSupport';
import { prefetchPatientRoute } from '../../router/patient/patientLoaders';

interface AuthenticatedNavbarProps {
  openNavbarMobile: () => void;
}

export const AuthenticatedNavbar: React.FC<AuthenticatedNavbarProps> = ({
  openNavbarMobile,
}) => {
  const [isOpenCart, setIsOpenCart] = useState(false);

  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.NAVBAR_COMPONENT,
  });
  const { providersSelectionDisabled, appointmentsSelectionDisabled } =
    useFlags<CodexFeatureFlags>();

  const {
    loadingOptions,
    MySkinOptions,
    ManageTestKitsRoutes,
    ManageTestKitsOptions,
    MySkinRoutes,
    MyAccountRoutes,
    AppointmentsRoutes,
    AppointmentsOptions,
  } = useNavbarOptions();

  const { selectedAccount, user } = useAuth();

  const { loggedUserIndicator } = useIndicators();

  const { data } = useGetCartProductsQuery({
    skip: !user?.mfaPhoneNumber,
  });
  const cartItems = useMemo(() => {
    const items = (data?.getCartProducts?.cart_products ?? []).reduce(
      (prev, next) => prev + next.quantity,
      0,
    );
    return items > 99 ? '99' : items;
  }, [data]);

  const [selectedSection, setSelectedSection] = useState<Section>();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const route = location.pathname.split('/')[1];
    setSelectedSection((route as Section) || Section.Dashboard);
  }, [location]);

  const {
    openDropdownId,
    handleDropdownClick,
    registerDropdownRef,
    handleHoverOut,
  } = useDropdown();

  const {
    openDropdownId: isOpenNotificationsDropdown,
    handleDropdownClick: handleNotificationsDropdownClick,
    handleHoverOut: handleNotificationsDropdownHoverOut,
  } = useDropdown();

  const [clearAllSavedDnaScannedDataForUserIdMutation] =
    useClearAllSavedDnaScannedDataForUserIdMutation({
      variables: {
        userId: user?.uuid || '',
      },
    });

  const handleDropdownOptionSelected = async (
    event: React.MouseEvent<HTMLElement>,
    section: Section,
    subRoute: string,
  ) => {
    //stopping propagation in order to not fire the handlePrincipalMenuClicked and navigate again
    event.stopPropagation();

    if (subRoute === MyAccountRoutes[locale?.myAccountOptions.logOut]) {
      if (user?.role === Roles.PATIENT) {
        await clearAllSavedDnaScannedDataForUserIdMutation().catch((e) =>
          console.log('Error on clearing DNA saved data', e),
        );
      }
      navigate(LOGOUT);
    } else if (
      section === Section.MyAccount ||
      section === Section.ManageTestKits ||
      section === Section.Providers ||
      section === Section.Appointments ||
      section === Section.MySkin
    ) {
      navigate(subRoute);
    } else {
      navigate(`/${section}/${subRoute}`);
    }
  };

  const handlePrincipalMenuClicked = (section: string) => {
    switch (section) {
      case Section.MySkin:
        navigate(MY_SKIN_MY_VISIT_SUMMARIES);
        break;
      case Section.ManageTestKits:
        navigate(TEST_KITS_MANAGEMENT_TEST_KITS);
        break;
      case Section.Appointments:
        navigate(MY_APPOINTMENTS);
        break;
      case Section.Providers:
        navigate(PROVIDERS);
        break;
      case Section.Shop:
        navigate(SHOP);
        break;
      case Section.Courses:
        navigate(COURSES);
        break;
      case Section.MyAccount:
        navigate(MY_ACCOUNT_PROFILE);
        break;
      default:
        break;
    }

    handleDropdownClick(section);
  };

  const isSectionSelected = (section: Section): boolean =>
    selectedSection === section;

  const { internalNotificationCount: notifications } = useNotificationCount();

  if ((loading && !locale) || loadingOptions) return null;

  return (
    <div className="flex flex-row justify-between w-full h-full">
      <PatientChatModal />

      <NavLink
        to={DASHBOARD}
        onMouseEnter={() => prefetchPatientRoute(DASHBOARD)}
        className="hidden desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement"
        data-testid="dashboard-nav"
      >
        <div className="flex w-full h-[84px] items-center pt-1.5">
          <div
            className={`text-base font-bold ${
              isSectionSelected(Section.Dashboard)
                ? 'text-clc-blue'
                : 'text-med-gray'
            }`}
          >
            {locale?.dashboard}
          </div>
        </div>
        <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
        <div
          className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
            isSectionSelected(Section.Dashboard) ? 'bg-clc-blue' : ''
          }`}
        />
      </NavLink>
      <div
        className="hidden desktop:flex flex-col cursor-pointer relative navBarPrimaryNavigationElement"
        onClick={() => handlePrincipalMenuClicked(Section.MySkin)}
        onMouseEnter={() => {
          handleDropdownClick(Section.MySkin);
          prefetchPatientRoute(Section.MySkin);
        }}
        onMouseLeave={() => handleHoverOut()}
        data-testid="care-history-nav"
      >
        <div ref={(ref) => registerDropdownRef(Section.MySkin, ref)}>
          <div className="flex w-full h-[84px] items-center pt-1.5">
            <div
              className={`text-base uppercase font-bold flex items-center ${
                isSectionSelected(Section.MySkin)
                  ? 'text-clc-blue'
                  : 'text-med-gray'
              }`}
            >
              <span className="mr-[5px] uppercase">{locale?.mySkin}</span>
              <ChevronDownIcon className="w-3 h-1.5 fill-current" />
            </div>
          </div>
          <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
          <div
            className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
              isSectionSelected(Section.MySkin) ? 'bg-clc-blue' : ''
            }`}
          />
          {openDropdownId === Section.MySkin && (
            <Dropdown
              zIndexClassName="z-[51]"
              options={MySkinOptions}
              onClick={(event, option) =>
                handleDropdownOptionSelected(
                  event,
                  Section.MySkin,
                  MySkinRoutes[option].toString(),
                )
              }
            />
          )}
        </div>
      </div>
      <div
        className="hidden desktop:flex flex-col cursor-pointer relative navBarPrimaryNavigationElement"
        onClick={() => handlePrincipalMenuClicked(Section.ManageTestKits)}
        onMouseEnter={() => {
          handleDropdownClick(Section.ManageTestKits);
          prefetchPatientRoute(Section.ManageTestKits);
        }}
        onMouseLeave={() => handleHoverOut()}
        data-testid="my-tests-nav"
      >
        <div ref={(ref) => registerDropdownRef(Section.ManageTestKits, ref)}>
          <div className="flex w-full h-[84px] items-center pt-1.5">
            <div
              className={`text-base font-bold flex items-center ${
                isSectionSelected(Section.ManageTestKits)
                  ? 'text-clc-blue'
                  : 'text-med-gray'
              }`}
            >
              <span className="mr-[5px]">{locale?.manageTestKits}</span>
              <ChevronDownIcon className="w-3 h-1.5 fill-current" />
            </div>
          </div>
          <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
          <div
            className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
              isSectionSelected(Section.ManageTestKits) ? 'bg-clc-blue' : ''
            }`}
          />
          {openDropdownId === Section.ManageTestKits && (
            <Dropdown
              zIndexClassName="z-[51]"
              options={ManageTestKitsOptions}
              onClick={(event, option) =>
                handleDropdownOptionSelected(
                  event,
                  Section.ManageTestKits,
                  ManageTestKitsRoutes[option].toString(),
                )
              }
            />
          )}
        </div>
      </div>

      {!appointmentsSelectionDisabled && (
        <div
          className="hidden desktop:flex flex-col cursor-pointer relative navBarPrimaryNavigationElement"
          onClick={() => handlePrincipalMenuClicked(Section.Appointments)}
          onMouseEnter={() => {
            handleDropdownClick(Section.Appointments);
            prefetchPatientRoute(Section.Appointments);
          }}
          onMouseLeave={() => handleHoverOut()}
          data-testid="appointments-nav"
        >
          <div ref={(ref) => registerDropdownRef(Section.Appointments, ref)}>
            <div className="flex h-[84px] items-center pt-1.5">
              <div
                className={`text-base font-bold flex items-center ${
                  isSectionSelected(Section.Appointments)
                    ? 'text-clc-blue'
                    : 'text-med-gray'
                }`}
              >
                <span className="mr-[5px]">{locale?.appointments}</span>
                <ChevronDownIcon className="w-3 h-1.5 fill-current" />
              </div>
            </div>
            <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
            <div
              className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
                isSectionSelected(Section.Appointments) ? 'bg-clc-blue' : ''
              }`}
            />
            {openDropdownId === Section.Appointments && (
              <Dropdown
                zIndexClassName="z-[51]"
                options={AppointmentsOptions}
                onClick={(event, option) =>
                  handleDropdownOptionSelected(
                    event,
                    Section.Appointments,
                    AppointmentsRoutes[option].toString(),
                  )
                }
              />
            )}
          </div>
        </div>
      )}

      {!providersSelectionDisabled && (
        <div
          className="hidden desktop:flex flex-col cursor-pointer relative navBarPrimaryNavigationElement"
          onClick={() => handlePrincipalMenuClicked(Section.Providers)}
          onMouseEnter={() => {
            handleDropdownClick(Section.Providers);
            prefetchPatientRoute(Section.Providers);
          }}
          onMouseLeave={() => handleHoverOut()}
          data-testid="providers-nav"
        >
          <div ref={(ref) => registerDropdownRef(Section.Providers, ref)}>
            <div className="flex h-[84px] items-center pt-1.5">
              <div
                className={`text-base font-bold flex items-center ${
                  isSectionSelected(Section.Providers)
                    ? 'text-clc-blue'
                    : 'text-med-gray'
                }`}
              >
                <span className="mr-[5px]">{locale?.providers}</span>
              </div>
            </div>
            <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
            <div
              className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
                isSectionSelected(Section.Providers) ? 'bg-clc-blue' : ''
              }`}
            />
          </div>
        </div>
      )}

      <div
        className="hidden desktop:flex flex-col cursor-pointer relative navBarPrimaryNavigationElement"
        onClick={() => handlePrincipalMenuClicked(Section.Courses)}
        onMouseEnter={() => {
          handleDropdownClick(Section.Courses);
          prefetchPatientRoute(Section.Courses);
        }}
        onMouseLeave={() => handleHoverOut()}
        data-testid="courses-nav"
      >
        <div ref={(ref) => registerDropdownRef(Section.Courses, ref)}>
          <div className="flex h-[84px] items-center pt-1.5">
            <div
              className={`text-base font-bold flex uppercase items-center ${
                isSectionSelected(Section.Courses)
                  ? 'text-clc-blue'
                  : 'text-med-gray'
              }`}
            >
              <span className="mr-[5px]">{'Education'}</span>
            </div>
          </div>
          <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
          <div
            className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
              isSectionSelected(Section.Courses) ? 'bg-clc-blue' : ''
            }`}
          />
        </div>
      </div>

      <div className="my-auto">
        <div className="hidden desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement">
          <div className="flex h-[84px] items-center">
            <ContactSupport />
          </div>
          <div className="absolute -left-0 -bottom-1 w-full h-[30px] navBarHoverDisplay" />
        </div>
      </div>

      <NavLink
        to={MESSAGES}
        onMouseEnter={() => prefetchPatientRoute(MESSAGES)}
        className="hidden desktop:flex desktop:flex-col cursor-pointer relative navBarPrimaryNavigationElement"
        data-testid={'link-messages'}
      >
        <div className="flex h-[84px] items-center pt-1.5 relative">
          {loggedUserIndicator?.inMail && (
            <div className="absolute z-10 top-8 -right-0.5 border border-white h-2.5 w-2.5 rounded-full bg-orange-body-dot" />
          )}
          <EmailGrayIcon
            className={`${
              isSectionSelected(Section.Messages)
                ? 'text-clc-blue'
                : 'text-med-gray'
            }`}
          />
        </div>

        <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
        <div
          className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
            isSectionSelected(Section.Messages) ? 'bg-clc-blue' : ''
          }`}
        />
      </NavLink>

      <div className="hidden desktop:flex desktop:flex-row relative navBarPrimaryNavigationElement px-0">
        <div
          className="cursor-pointer relative"
          onClick={(event) => {
            event.stopPropagation();
            handleNotificationsDropdownClick('notifications', true);
          }}
          data-testid="notifications-nav"
          data-unread-status={notifications > 0}
        >
          <div className="flex h-[84px] items-center pt-1.5 px-3 relative">
            {notifications > 0 && (
              <div className="absolute top-[30px] right-2 border border-white h-2.5 w-2.5 rounded-full bg-orange-body-dot" />
            )}
            <NotificationIcon
              className={`${
                isSectionSelected(Section.Notifications) ||
                isOpenNotificationsDropdown === 'notifications'
                  ? 'fill-clc-blue'
                  : 'fill-med-gray'
              }`}
            />

            <div className="absolute left-0 -bottom-1.5 w-full h-[30px] navBarHoverDisplay" />
            <div
              className={`flex h-1.5 ${
                isSectionSelected(Section.Notifications) ? 'bg-clc-blue' : ''
              }`}
            />
            {
              <DropdownBodyContainer align={'right'}>
                {isOpenNotificationsDropdown === 'notifications' && (
                  <NotificationNavbarDropdown
                    isOpen
                    onClose={handleNotificationsDropdownHoverOut}
                  />
                )}
              </DropdownBodyContainer>
            }
          </div>
        </div>
      </div>

      <div className="hidden group desktop:flex desktop:flex-row navBarPrimaryNavigationElement px-0">
        <div
          className="cursor-pointer relative flex px-3"
          onClick={(event) => {
            event.stopPropagation();
            setIsOpenCart((bool) => !bool);
          }}
          data-testid="shop-nav"
        >
          <Shop className="h-6 w-6 min-h-[24px] fill-med-gray-3 my-auto" />
          {!!cartItems && (
            <span className="absolute w-4 h-4 text-center text-xs z-10 top-[1.63rem] right-1.5 rounded-full bg-orange-body-dot text-white pr-1.5 pl-[5px]">
              {cartItems}
            </span>
          )}
          <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
        </div>
      </div>
      <div className="ml-4">
        <div
          className="hidden desktop:flex flex-col cursor-pointer relative"
          onMouseEnter={() => prefetchPatientRoute(Section.MyAccount)}
          onClick={() => handleDropdownClick(Section.MyAccount)}
          data-testid="profile-nav"
        >
          <div ref={(ref) => registerDropdownRef(Section.MyAccount, ref)}>
            <div className="flex h-[84px] items-center justify-center pt-1 px-0">
              <AccountCard
                id={selectedAccount || user?.uuid || ''}
                widthClasses="w-[244px]"
                profilePicture={user?.profilePicture}
                name={user?.displayName as string}
                isDropdown
                isFocused={openDropdownId === Section.MyAccount}
              />
            </div>
            <AccountDropdown
              isOpen={openDropdownId === Section.MyAccount}
              locale={locale}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col desktop:hidden cursor-pointer relative">
        <NavLink
          to={NOTIFICATIONS}
          className="cursor-pointer relative"
          onMouseEnter={() => prefetchPatientRoute(NOTIFICATIONS)}
        >
          <div className="flex h-[70px] w-[45px] items-center justify-center">
            {notifications > 0 && (
              <div className="absolute top-5 right-2 h-2 w-2 rounded-full bg-orange-body-dot" />
            )}
            <NotificationIcon
              className={`h-6 w-6 ${
                isSectionSelected(Section.Notifications)
                  ? 'fill-clc-blue'
                  : 'fill-med-gray'
              }`}
            />
          </div>
          <div className="absolute -left-0 bottom-0 w-full h-[30px] navBarHoverDisplay" />
          <div
            className={`h-1.5 w-[calc(100%_+_1.5rem)] -ml-3 ${
              isSectionSelected(Section.Notifications) ? 'bg-clc-blue' : ''
            }`}
          />
        </NavLink>
      </div>
      <PatientAvatarMobile
        openNavbarMobile={openNavbarMobile}
        profilePicture={user?.profilePicture}
      />
      <div className="flex desktop:hidden">
        <div className="flex h-[70px] desktop:h-[84px] w-[45px] desktop:w-16 items-center justify-center desktop:pt-1 desktop:px-0">
          <MenuHamburgerIcon
            className="h-8 w-[45px] cursor-pointer"
            onClick={openNavbarMobile}
          />
        </div>
      </div>

      <ShopModal isOpenCart={isOpenCart} setIsOpenCart={setIsOpenCart} />
    </div>
  );
};
